"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search, User, Menu, X, Loader, ShoppingBag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { cn } from "@/lib/utils";
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="5804" className="flex items-center justify-center p-4 text-slate-400">
        <Loader className="animate-spin size-5 mr-2" />
        <span className="text-sm font-medium tracking-tight">Searching Database...</span>
      </div>;
  }
  if (error) {
    return <p className="p-4 text-sm text-destructive font-medium">Failed to load catalog.</p>;
  }
  if (!products.length) {
    return <p className="p-4 text-sm text-slate-500 font-medium">No sequences found.</p>;
  }
  return <div className="max-h-[60vh] overflow-y-auto py-2">
      {products.map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 p-3 hover:bg-muted transition-colors border-b last:border-0 border-border/50" data-index={i}>
          <div className="size-12 shrink-0 bg-muted rounded-sm overflow-hidden">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300" /> : <div className="w-full h-full flex items-center justify-center bg-muted text-foreground">
                <ShoppingBag className="size-5 opacity-30" />
              </div>}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium truncate text-foreground tracking-tight">{product.title}</h4>
            <div className="flex items-center gap-2 mt-0.5">
              {product.discounted_price ? <>
                  <span className="text-xs font-bold text-primary">${product.discounted_price}</span>
                  <span className="text-xs text-slate-400 line-through">${product.price}</span>
                </> : <span className="text-xs font-bold text-foreground">${product.price}</span>}
            </div>
          </div>
        </Link>)}
    </div>;
}
function SearchWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return <div ref={containerRef} className="relative">
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="text-foreground hover:text-primary hover:bg-transparent transition-all duration-300 hover:scale-110" aria-label="Search">
        <Search className="size-6 transition-transform duration-200 group-hover:scale-110" />
      </Button>

      {isOpen && <div className="absolute right-0 top-full mt-2 w-[320px] sm:w-[400px] bg-background border border-border shadow-xl rounded-sm overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
          <div className="p-3 border-b border-border bg-muted/30">
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search research compounds..." className="w-full border-none focus-visible:ring-0 text-sm" />
          </div>
          <div className="bg-background">
            {!hasSearchQuery ? <div className="p-8 text-center text-sm text-slate-500">
                <Search className="size-8 mx-auto mb-2 opacity-10" />
                <p className="font-medium tracking-tight">Search our verified catalog</p>
              </div> : <EcommerceProductsProvider wp_query={wp_query} postType="product">
                <SearchResults />
              </EcommerceProductsProvider>}
          </div>
        </div>}
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted rounded-sm" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-9 w-max items-center justify-center rounded-sm bg-transparent px-3 py-1.5 text-sm font-300 tracking-widest transition-all hover:text-primary hover:font-400 focus:outline-none disabled:pointer-events-none disabled:opacity-50">
                {item.label}
                <span className="absolute bottom-1.5 left-3 right-3 h-px bg-primary scale-x-0 transition-transform duration-200 group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-4 p-4">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-6 w-3/4 bg-muted animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col p-4 space-y-0">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="block px-4 py-2.5 text-base font-300 tracking-wide text-foreground hover:bg-muted hover:text-primary rounded-sm transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <div className="h-px bg-border my-4 mx-4" />
      <Link to="#" className="flex items-center px-4 py-2.5 text-base font-300 tracking-wide text-foreground hover:bg-muted hover:text-primary rounded-sm transition-colors" onClick={onClose}>
        <User className="mr-3 size-5" />
        Account Portal
      </Link>
    </nav>;
}
export default function PrintTeeMainHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="signal-source-one-global-header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-background border-border")}>
      <div className="container mx-auto px-4 h-20 md:h-24 flex items-center justify-between gap-4">
        <div className="flex-shrink-0 flex items-center">
          <Link to="/" className="block hover:opacity-80 transition-opacity">
            <img src="https://signalsourceone.com/wp-content/uploads/2026/05/signal_source_one_transparent_header-scaled.png" alt="Signal Source One" className="h-full w-auto object-contain" />
          </Link>
        </div>

        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="60" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <SearchWidget />

          <Button variant="ghost" size="icon" asChild className="hidden md:inline-flex text-foreground hover:text-primary hover:bg-transparent transition-transform duration-200 hover:scale-110">
            <Link to="#" aria-label="My Account">
              <User className="size-6" />
            </Link>
          </Button>

          <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-transparent transition-all duration-200 hover:scale-110" iconSize="size-6" />

          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-transparent transition-transform duration-200 hover:scale-110" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-8" /> : <Menu className="size-8" />}
          </Button>
        </div>
      </div>

      <div className="bg-muted/50 text-muted-foreground text-center py-1 border-t border-border/40">
        <span className="text-[9px] font-bold tracking-widest uppercase">
          Research Use Only - Not for Human Consumption
        </span>
      </div>

      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-xl md:hidden animate-in slide-in-from-top-5 duration-200 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <MenuProvider menu_id="60">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}